import { setup, assign } from "xstate"
import type { OwnerOutput } from "@core/composables"

// ================================
// TYPES
// ================================

/** Payload PCE_EXIST (champs de PceFormSearchExistPayload sauf type source) */
export type PceExistPayload = {
    exists: boolean
    pce: string
    invitation?: boolean
    consentementUser?: boolean
    _id_source?: string
    dda: string
    date_from_data?: string
    /** Email titulaire = signataire/titulaire consentement → SHARE (invitation, sans accès) */
    sameTitulaireEmail?: boolean
}

export type FormPceEvent =
    | { type: "START" }
    | { type: "OWNER_SUBMIT"; owner: OwnerOutput }
    | ({ type: "PCE_EXIST" } & PceExistPayload)
    | {
          type: "CONSENT_SIGNED"
          _id_source?: string
          _id_consent?: string
          contractName?: string
          taskOnboarding?: unknown
      }
    | { type: "GRDF_DONE" }
    | {
          type: "INVITATION_SENT"
          _id_consentrequest?: string
          url?: string
          _id_source?: string
      }
    | {
          type: "ASK_SHARING_SENT"
          _id_consentrequest?: string
          url?: string
      }
    | { type: "ALREADY_EXIST" }
    | { type: "EXIST_ERROR" }
    | { type: "RETRY" }
    | { type: "BACK" }
    | { type: "UPDATE_FORM_DONE" }

export interface FormPceContext {
    invitation?: boolean
    /** Titulaire remonté par SourceFormOwner */
    owner?: OwnerOutput
    /** Numéro PCE saisi dans PceFormSearch */
    pce?: string
    /** Identifiant de la source existante (consentementUser, pour SourceAlreadyExist) ou créée après signature (SourceConsentSign) */
    _id_source?: string
    /** Identifiant du consentement créé après signature (pour déclaration GRDF) */
    _id_consent?: string
    /** Nom du contrat (après signature consentement, aligné PDL) */
    contractName?: string
    /** Statut du droit d'accès GRDF (DDA) */
    dda?: string
    /** Date de début de récupération des données */
    date_from_data?: string
    /** Dernier résultat « exist » (hors date) pour réafficher PceFormSearch au retour arrière */
    pceSearchExists?: boolean
    /** Dernier consentementUser du payload PCE_EXIST (réhydratation recherche) */
    pceSearchConsentementUser?: boolean
    /** Identifiant de la demande créée lors de SourceInvitationSign */
    _id_consentrequest?: string
    /** URL de la demande créée lors de SourceInvitationSign */
    url?: string
    /** Tâche onboarding bâtiment après PUT sign (`task_building`) */
    taskOnboarding?: unknown
}

// ================================
// MACHINE
// ================================

/**
 * Machine du parcours d'ajout PCE (compteur gaz).
 * `invitation` = client externe (`!owner.isConnectedUser`), comme le PDL.
 * États : idle → SourceFormOwner → PceFormSearch → PceFormWaitingDda | SourceConsentSign | PceFormGrdfDeclaration | SourceInvitationSign | SourceInvitationShare | SourceAlreadyExist | SourceExistError → closed
 */
export function createFormPceMachine() {
    return setup({
        types: {} as { context: FormPceContext; events: FormPceEvent },
        actions: {
            /** Client externe = invitation true (même règle que createFormPdlMachine.setOwner) */
            setOwner: assign({
                owner: ({ event }) =>
                    event.type === "OWNER_SUBMIT" ? event.owner : undefined,
                invitation: ({ event }) =>
                    event.type === "OWNER_SUBMIT"
                        ? !event.owner?.isConnectedUser
                        : undefined,
            }),
            setPceExist: assign({
                pce: ({ event }) =>
                    event.type === "PCE_EXIST" ? event.pce : undefined,
                _id_source: ({ event }) =>
                    event.type === "PCE_EXIST" && event._id_source != null
                        ? event._id_source
                        : undefined,
                invitation: ({ event }) =>
                    event.type === "PCE_EXIST" ? event.invitation : undefined,
                dda: ({ event }) =>
                    event.type === "PCE_EXIST" ? event.dda : undefined,
                date_from_data: ({ event }) =>
                    event.type === "PCE_EXIST"
                        ? event.date_from_data
                        : undefined,
                pceSearchExists: ({ event }) =>
                    event.type === "PCE_EXIST" ? event.exists : undefined,
                pceSearchConsentementUser: ({ event }) =>
                    event.type === "PCE_EXIST"
                        ? event.consentementUser
                        : undefined,
                sameTitulaireEmail: ({ event }) =>
                    event.type === "PCE_EXIST"
                        ? event.sameTitulaireEmail
                        : undefined,
            }),
            setInvitationResult: assign({
                _id_consentrequest: ({ event }) =>
                    event.type === "INVITATION_SENT" &&
                    event._id_consentrequest != null
                        ? event._id_consentrequest
                        : undefined,
                url: ({ event }) =>
                    event.type === "INVITATION_SENT" && event.url != null
                        ? event.url
                        : undefined,
                _id_source: ({ event }) =>
                    event.type === "INVITATION_SENT" && event._id_source != null
                        ? event._id_source
                        : undefined,
            }),
            setAskSharingResult: assign({
                _id_consentrequest: ({ event }) =>
                    event.type === "ASK_SHARING_SENT" &&
                    event._id_consentrequest != null
                        ? event._id_consentrequest
                        : undefined,
                url: ({ event }) =>
                    event.type === "ASK_SHARING_SENT" && event.url != null
                        ? event.url
                        : undefined,
            }),
            setConsentSigned: assign({
                _id_source: ({ event }) =>
                    event.type === "CONSENT_SIGNED" && event._id_source != null
                        ? event._id_source
                        : undefined,
                _id_consent: ({ event }) =>
                    event.type === "CONSENT_SIGNED" && event._id_consent != null
                        ? event._id_consent
                        : undefined,
                contractName: ({ event }) =>
                    event.type === "CONSENT_SIGNED"
                        ? event.contractName
                        : undefined,
                taskOnboarding: ({ event }) =>
                    event.type === "CONSENT_SIGNED"
                        ? event.taskOnboarding
                        : undefined,
            }),
        },
    }).createMachine({
        id: "form-pce",
        initial: "idle",
        context: {},
        states: {
            idle: {
                on: {
                    START: "SourceFormOwner",
                },
            },
            SourceFormOwner: {
                on: {
                    OWNER_SUBMIT: {
                        target: "PceFormSearch",
                        actions: "setOwner",
                    },
                    BACK: "idle",
                },
            },
            PceFormSearch: {
                on: {
                    PCE_EXIST: [
                        {
                            target: "PceFormWaitingDda",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" &&
                                !event.exists &&
                                event.dda === "not_found_source_with_valid_dda",
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceInvitationSign",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" &&
                                !event.exists &&
                                !!event.invitation,
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceConsentSign",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" && !event.exists,
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceAlreadyExist",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" &&
                                !!event.exists &&
                                !!event.consentementUser,
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceInvitationShare",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" &&
                                !!event.exists &&
                                !!event.invitation &&
                                !event.consentementUser &&
                                !!event.sameTitulaireEmail,
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceInvitationSign",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" &&
                                !!event.exists &&
                                !!event.invitation &&
                                !event.consentementUser,
                            actions: "setPceExist",
                        },
                        {
                            target: "SourceExistError",
                            guard: ({ event }) =>
                                event.type === "PCE_EXIST" && !!event.exists,
                            actions: "setPceExist",
                        },
                    ],
                    BACK: "SourceFormOwner",
                },
            },
            SourceConsentSign: {
                on: {
                    CONSENT_SIGNED: {
                        target: "PceFormGrdfDeclaration",
                        actions: "setConsentSigned",
                    },
                    BACK: "PceFormSearch",
                },
            },
            PceFormGrdfDeclaration: {
                on: {
                    GRDF_DONE: "closed",
                    BACK: "SourceConsentSign",
                },
            },
            PceFormWaitingDda: {
                on: {
                    BACK: "PceFormSearch",
                },
            },
            SourceInvitationSign: {
                on: {
                    // NB (DEV-16347) : nom trompeur. INVITATION_SENT = invitation
                    // GÉNÉRÉE (lien créé), pas email envoyé. L'envoi réel se fait
                    // dans SourceInvitationSignSent. À renommer si on retouche le flux.
                    INVITATION_SENT: {
                        target: "SourceInvitationSignSent",
                        actions: "setInvitationResult",
                    },
                    BACK: "PceFormSearch",
                },
            },
            SourceInvitationSignSent: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                },
            },
            SourceInvitationShare: {
                on: {
                    ASK_SHARING_SENT: {
                        target: "SourceInvitationShareSent",
                        actions: "setAskSharingResult",
                    },
                    BACK: "PceFormSearch",
                },
            },
            SourceInvitationShareSent: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                },
            },
            SourceAlreadyExist: {
                on: {
                    UPDATE_FORM_DONE: "closed",
                    BACK: "PceFormSearch",
                },
            },
            SourceExistError: {
                on: {
                    RETRY: "PceFormSearch",
                    BACK: "PceFormSearch",
                },
            },
            closed: {
                type: "final",
            },
        },
    })
}

export type FormPceMachine = ReturnType<typeof createFormPceMachine>
